import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { fetchLabels } from "../../../utils/api";
import {
    fetchLabels as fetchLabelsApi,
    createLabel,
    updateLabel,
    deleteLabel
} from '../api/labelApi';

export const useLabelData = (projectId) => {
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadLabels = async () => {
            if (!projectId) return;

            setLoading(true);
            setError(null);

            try {
                const data = await fetchLabels(projectId);

                const noneLabel = data.find((label) => label.name === "ラベルなし");
                const restLabels = data.filter((label) => label.name !== "ラベルなし");
                const sortedLabels = noneLabel ? [noneLabel, ...restLabels] : restLabels;

                setLabels(sortedLabels);
            } catch (err) {
                console.error("Failed to fetch labels:", err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        loadLabels();
    }, [projectId]);

    return { labels, loading, error };
};

export const useSelectionState = (labels, initialValue, onChange) => {
    const initialLoad = useRef(true);

    useEffect(() => {
        if (initialLoad.current && labels.length > 0 && initialValue.length === 0) {
            onChange(labels.map((label) => label.id));
            initialLoad.current = false;
        }
    }, [labels, initialValue, onChange]);

    const allSelected = useMemo(() =>
        labels.length > 0 && initialValue.length === labels.length,
        [labels.length, initialValue.length]
    );

    const toggleAll = useCallback(() => {
        onChange(allSelected ? [] : labels.map((label) => label.id));
    }, [allSelected, labels, onChange]);

    const toggleOne = useCallback((id) => {
        onChange(initialValue.includes(id)
            ? initialValue.filter((value) => value !== id)
            : [...initialValue, id]
        );
    }, [initialValue, onChange]);

    return { allSelected, toggleAll, toggleOne };
};

export const useLabels = (projectId) => {
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState({ text: "", type: "" });

    const loadLabels = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchLabelsApi(projectId);
            setLabels(data);
            setError(null);
        } catch (err) {
            setError(err);
            setMessage({ text: "ラベルの読み込みに失敗しました", type: "error" });
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        if (projectId) loadLabels();
    }, [projectId, loadLabels]);

    const isDuplicateLabel = useCallback((name, excludeId = null) => {
        return labels.some(label =>
            label.name.toLowerCase().trim() === name.toLowerCase().trim() &&
            label.id !== excludeId
        );
    }, [labels]);

    const addLabel = useCallback(async (data) => {
        try {
            if (isDuplicateLabel(data.name)) {
                setMessage({ text: "同じ名前のラベルが既に存在します", type: "error" });
                return false;
            }

            await createLabel(projectId, data);
            await loadLabels();
            setMessage({ text: "ラベルを追加しました", type: "success" });
            return true;
        } catch (err) {
            setMessage({ text: "ラベルの追加に失敗しました", type: "error" });
            return false;
        }
    }, [projectId, loadLabels, isDuplicateLabel]);

    const editLabel = useCallback(async (id, data) => {
        try {
            if (isDuplicateLabel(data.name, id)) {
                setMessage({ text: "同じ名前のラベルが既に存在します", type: "error" });
                return false;
            }

            await updateLabel(projectId, id, data);
            await loadLabels();
            setMessage({ text: "ラベルを更新しました", type: "success" });
            return true;
        } catch (err) {
            setMessage({ text: "ラベルの更新に失敗しました", type: "error" });
            return false;
        }
    }, [projectId, loadLabels, isDuplicateLabel]);

    const removeLabel = useCallback(async (id) => {
        try {
            await deleteLabel(projectId, id);
            await loadLabels();
            setMessage({ text: "ラベルを削除しました", type: "success" });
            return true;
        } catch (err) {
            setMessage({ text: "ラベルの削除に失敗しました", type: "error" });
            return false;
        }
    }, [projectId, loadLabels]);

    const clearMessage = useCallback(() => {
        setMessage({ text: "", type: "" });
    }, []);

    return {
        labels,
        loading,
        error,
        message,
        addLabel,
        editLabel,
        removeLabel,
        clearMessage,
        isDuplicateLabel,
        loadLabels,
    };
};